// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';

import { Grid, Link, Paper, Card, CardContent } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import {
  DataGrid,
  GridColDef,
  GridSelectionModel,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';
import RoomIcon from '@mui/icons-material/Room';
import { Typography, Snackbar, Button } from '@mui/material';
import Alert, { AlertProps } from '@mui/material/Alert';
import DeleteForeverTwoToneIcon from '@material-ui/icons/DeleteForeverTwoTone';

import { COLOR_RED, mapOptions, exchangeRates, BACKGROUND_COLOR } from 'src/const';
import { convertCurrency, formatCurrency, makeLowerCase } from 'src/utils/functions';
import { InfoTooltip } from '../../../HOC/Tooltips';
import { TooltipCard, LoadingCard } from '../../../HOC';
import { MyAnalysisDashboardPropTypes } from 'src/interfaces/iproptypes';
import { RegionMetadata, SimulationSnapshot, WorkbookData } from 'src/interfaces/idashboard';
import { callApi } from 'src/core/callApi';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mapContainer: {
      height: 400,
    },
    workbookContainer: {
      minHeight: 400,
      width: '100vw',
    },
  })
);

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
};

const getInvestmentCalculatorInputsQuery = (workbook: WorkbookData) => {
  const {
    propertyPrice,
    managementFee,
    propertyTax,
    lodgingTax,
    mortgageFees,
    hoaFees,
    maintenance,
    insurance,
    electricity,
    wifi,
    gas,
    other,
    homeFurnishings,
    closingCosts,
    currency,
  } = workbook;
  const investmentCalculatorInputsQuery = `&propertyPrice=${propertyPrice}&managementFee=${managementFee}&propertyTax=${propertyTax}&lodgingTax=${lodgingTax}&mortgageFees=${mortgageFees}&hoaFees=${hoaFees}&maintenance=${maintenance}&insurance=${insurance}&electricity=${electricity}&wifi=${wifi}&gas=${gas}&other=${other}&homeFurnishings=${homeFurnishings}&closingCosts=${closingCosts}&currency=${currency}`;
  return investmentCalculatorInputsQuery;
};

const MyAnalysisDashboard = ({ user, getUserInfo, matches }: MyAnalysisDashboardPropTypes) => {
  const classes = useStyles();

  const [pointers, setPointers] = useState([] as RegionMetadata[]);
  const [centerLat, setCenterLat] = useState(36.513172);
  const [centerLng, setCenterLng] = useState(-392.258314);

  if ((window as any).Tawk_API) {
    (window as any).Tawk_API.onLoad = () => {
      (window as any).Tawk_API.hideWidget();
    };
  }

  const workbookCols: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params: any) => (
        <div>
          {params.row.link ? (
            <Link href={params.row.link} target="_blank">
              {params.row.name}
            </Link>
          ) : (
            params.row.name
          )}
        </div>
      ),
      editable: true,
    },
    {
      field: 'propertyPrice',
      headerName: 'Property Price',
      width: 200,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[params.row.currency].symbol}
          {formatCurrency(params.row.propertyPrice.toString())}
        </Typography>
      ),
      editable: true,
    },
    {
      field: 'market',
      headerName: 'Market',
      width: 200,
      renderCell: (params: any) => {
        const bedrooms = params.row.bedrooms;
        const bathrooms = params.row.bathrooms;
        const investmentCalculatorInputsQuery = getInvestmentCalculatorInputsQuery(params.row);
        const addressQuery = params.row.address ? `&estimatorAddress=${params.row.address}` : '';
        const estimatorLink = `${window.location.origin}/airbnb-calculator?estimatorCoord=${params.row.coordinates}${addressQuery}&bedrooms=${bedrooms}&bathrooms=${bathrooms}${investmentCalculatorInputsQuery}`;
        return (
          <Link href={estimatorLink} target="_blank">
            {params.row.market}
          </Link>
        );
      },
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      width: 200,
      editable: true,
    },
    {
      field: 'bathrooms',
      headerName: 'Bathrooms',
      width: 200,
      editable: true,
    },
    {
      field: 'grossRentalYield',
      headerName: 'Gross Rental Yield',
      width: 200,
      renderCell: (params: any) => <Typography>{params.row.grossRentalYield}%</Typography>,
      editable: true,
    },
    {
      field: 'netRentalYield',
      headerName: 'Net Rental Yield',
      width: 200,
      renderCell: (params: any) => <Typography>{params.row.netRentalYield}%</Typography>,
      editable: true,
    },
    {
      field: 'netProfitBeforeTax',
      headerName: 'Net Profit Before Tax',
      width: 200,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[params.row.currency].symbol}
          {formatCurrency(
            convertCurrency(params.row.currency, params.row.netProfitBeforeTax).toString()
          )}
        </Typography>
      ),
      editable: true,
    },
    {
      field: 'downPayment',
      headerName: 'Down Payment',
      width: 200,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[params.row.currency].symbol}
          {formatCurrency(params.row.downPayment.toString())}
        </Typography>
      ),
      editable: true,
    },
    {
      field: 'cashOnCashReturn',
      headerName: 'Cash on Cash Return',
      width: 200,
      renderCell: (params: any) => <Typography>{params.row.cashOnCashReturn}%</Typography>,
      editable: true,
    },
    {
      field: 'annualRevenue',
      headerName: 'Annual Revenue',
      width: 200,
      renderCell: (params: any) => {
        return (
          <Typography>
            {exchangeRates[params.row.currency].symbol}
            {formatCurrency(
              convertCurrency(params.row.currency, params.row.annualRevenue).toString()
            )}
          </Typography>
        );
      },
      editable: true,
    },
    {
      field: 'occupancyRate',
      headerName: 'Occupancy Rate',
      width: 200,
      renderCell: (params: any) => <Typography>{params.row.occupancyRate}%</Typography>,
      editable: true,
    },
    {
      field: 'nightlyRate',
      headerName: 'Daily Rate',
      width: 200,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[params.row.currency].symbol}
          {formatCurrency(convertCurrency(params.row.currency, params.row.nightlyRate).toString())}
        </Typography>
      ),
      editable: true,
    },
    {
      field: 'averageStayLength',
      headerName: 'Average Length of Stay',
      width: 200,
    },
    {
      field: 'seasonality',
      headerName: 'Seasonality',
      width: 200,
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 200,
      renderCell: (params: any) => <Typography>{params.row.size}&#13217;</Typography>,
      editable: true,
    },
    {
      field: 'note',
      headerName: 'Note',
      width: 200,
      editable: true,
    },
  ];
  let workbookData: WorkbookData[] = [];
  const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, 'children' | 'severity'> | null>(
    null
  );
  const [deletedRows, setDeletedRows] = useState<number[]>([]);

  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  useEffect(() => {
    if (user.active_regions_metadata && user.active_regions_metadata.length > 0) {
      setPointers(user.active_regions_metadata);
      setCenterLat(user.active_regions_metadata[0].lat);
      setCenterLng(user.active_regions_metadata[0].lng);
    }
  }, [user]);

  if (user && user.workbooks) {
    const workbooks = standardizeWorkbooks(user.workbooks);
    workbookData = getWorkbookData(workbooks);
  }

  let markers = useMemo(() => {
    return pointers.map((pointer, index: number) => (
      <SubscribedMarketPointer key={index} data={pointer} lat={pointer.lat} lng={pointer.lng} />
    ));
  }, [pointers]);

  const handleCloseSnackbar = () => setSnackbar(null);

  const workbookCRUD = async (
    action: string,
    id: number,
    field: string,
    newValue: string,
    ids: number[]
  ) => {
    const workbookCrudUrl =
      'https://vdklywk2td.execute-api.us-east-2.amazonaws.com/default/workbookCRUD';
    let config = {};

    if (action === 'update') {
      config = {
        queryParams: {
          action: 'update',
          id: id,
          field: field,
          value: newValue,
        },
      };
    }

    if (action === 'delete') {
      config = {
        queryParams: {
          action: 'delete',
          ids: ids,
        },
      };
    }

    const response = await callApi(workbookCrudUrl, config, true);
    return response.message.status;
  };

  const updateCell = (params: GridCellEditStopParams, event: MuiEvent) => {
    let newVal = event.target.value;
    if (newVal) {
      const isInputValid = validateInput(params.field, newVal, params.value);
      if (isInputValid) {
        newVal = params.field === 'name' || params.field === 'note' ? newVal : parseFloat(newVal);
        workbookCRUD('update', params.id, params.field, newVal, null).then(status => {
          if (status === 'updated') {
            setSnackbar({
              children: 'Workbook successfully saved',
              severity: 'success',
            });
            getUserInfo();
          } else {
            setSnackbar({
              children: 'Failed to update workbook',
              severity: 'error',
            });
          }
        });
      } else {
        setSnackbar({
          children: 'Invalid input',
          severity: 'error',
        });
      }
    }
    getUserInfo();
  };

  const deleteSelectedRows = () => {
    if (deletedRows) {
      workbookCRUD('delete', null, null, null, deletedRows).then(status => {
        if (status === 'deleted') {
          setSnackbar({
            children: 'Workbook successfully deleted',
            severity: 'success',
          });
          getUserInfo();
        } else {
          setSnackbar({
            children: 'Failed to delete workbook',
            severity: 'error',
          });
        }
      });
    }
  };

  return (
    <Grid
      container
      direction="column"
      justify="flex-start"
      alignItems="flex-start"
      spacing={4}
      style={{ minHeight: '80vh', width: '100%', padding: matches ? '20px' : '40px' }}
    >
      {user.active_regions_metadata ? (
        <Grid item style={{ width: '100%' }}>
          <Card>
            <CardContent>
              <Typography style={{ marginBottom: 10 }}>My Markets</Typography>

              <Paper id="map" className={classes.mapContainer}>
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: GOOGLE_MAPS_API_KEY,
                    libraries: ['drawing', 'places', 'visualization'],
                    id: 'my-analysis-dashboard',
                  }}
                  center={{ lat: centerLat, lng: centerLng }}
                  options={mapOptions}
                  zoom={1}
                >
                  {markers}
                </GoogleMapReact>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      ) : (
        <LoadingCard height={400} />
      )}

      <Grid item style={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography style={{ marginBottom: 10 }}>My Workbooks</Typography>

            <div className={classes.workbookContainer}>
              <DataGrid
                autoHeight={true}
                pageSize={workbookData.length}
                hideFooterPagination={true}
                rows={workbookData}
                columns={workbookCols}
                disableColumnFilter={true}
                disableColumnMenu={true}
                onCellEditStop={updateCell}
                checkboxSelection
                onSelectionModelChange={(model: GridSelectionModel) => {
                  let selectedRows: number[] = [];
                  model.forEach((id: number) => {
                    selectedRows.push(id);
                  });
                  setDeletedRows(selectedRows);
                }}
                experimentalFeatures={{ newEditingApi: true }}
                components={{
                  Toolbar: CustomToolbar,
                }}
              />
              {!!snackbar && (
                <Snackbar
                  open
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  onClose={handleCloseSnackbar}
                  autoHideDuration={6000}
                >
                  <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
              )}
            </div>

            <Grid item container direction="row" justify="flex-start" style={{ marginTop: 10 }}>
              <Button style={{ background: 'white' }} onClick={deleteSelectedRows}>
                <DeleteForeverTwoToneIcon style={{ color: COLOR_RED }} />
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const SubscribedMarketPointer = ({
  data,
  lat,
  lng,
}: {
  data: RegionMetadata;
  lat: number;
  lng: number;
}) => {
  const district = data.name;
  const city = data.city ? data.city : '0';
  const country = data.country ? data.country : '0';

  return (
    <Link
      href={window.location.origin + `/airbnb-data/${country}/${city}/${district}`}
      target="_blank"
    >
      <InfoTooltip title={<TooltipCard content={data.name} />}>
        <RoomIcon style={{ fontSize: 28, color: COLOR_RED }} />
      </InfoTooltip>
    </Link>
  );
};

const getWorkbookData = (workbooks: SimulationSnapshot[]) => {
  let workbookData = [] as WorkbookData[];
  workbooks.forEach((workbook, index) => {
    const seasonality = getSeasonality(workbook.monthlyRevenues);

    workbookData.push({
      id: workbook.id,
      address: workbook.address,
      coordinates: workbook.coordinates,
      name: workbook.name,
      link: workbook.link,
      propertyPrice: workbook.propertyPrice,
      downPayment: workbook.downPayment,
      market: workbook.address != 'Set location on map' ? workbook.address : workbook.coordinates,
      averageStayLength:
        Math.round((((workbook.occupancyRate / 100) * 30.5) / workbook.bookingsPerMonth) * 10) / 10,
      seasonality: seasonality,
      bedrooms: workbook.bedrooms,
      bathrooms: workbook.bathrooms,
      size: workbook.size,
      annualRevenue: workbook.annualRevenue,
      nightlyRate: workbook.nightlyRate,
      occupancyRate: workbook.occupancyRate,
      grossRentalYield: workbook.grossRentalYield,
      netRentalYield: workbook.netRentalYield,
      note: workbook.note,
      managementFee: workbook.managementFee,
      propertyTax: workbook.propertyTax,
      lodgingTax: workbook.lodgingTax,
      mortgageFees: workbook.mortgageFees,
      hoaFees: workbook.hoaFees,
      maintenance: workbook.maintenance,
      insurance: workbook.insurance,
      electricity: workbook.electricity,
      wifi: workbook.wifi,
      gas: workbook.gas,
      other: workbook.other,
      homeFurnishings: workbook.homeFurnishings,
      closingCosts: workbook.closingCosts,
      netProfitBeforeTax: workbook.netProfitBeforeTax,
      cashOnCashReturn: workbook.cashOnCashReturn,
      currency: workbook.currency,
    });
  });
  return workbookData;
};

const getSeasonality = (monthlyRevenues: number[]) => {
  if (monthlyRevenues.length === 0) {
    return 0;
  }
  const highestRevenue = Math.max(...monthlyRevenues);
  const lowestRevenue = Math.min(...monthlyRevenues);
  const seasonality = Math.round(((highestRevenue - lowestRevenue) / highestRevenue) * 100);
  return seasonality;
};

const standardizeWorkbooks = (rawWorkbooks: SimulationSnapshot[]) => {
  let workbooks = [] as SimulationSnapshot[];
  rawWorkbooks.forEach(workbook => {
    workbooks.push({
      id: workbook.id,
      propertyPrice: workbook.propertyPrice ? workbook.propertyPrice : 0,
      downPayment: workbook.downPayment ? workbook.downPayment : 0,
      managementFee: workbook.managementFee ? workbook.managementFee : 0,
      propertyTax: workbook.propertyTax ? workbook.propertyTax : 0,
      lodgingTax: workbook.lodgingTax ? workbook.lodgingTax : 0,
      mortgageFees: workbook.mortgageFees ? workbook.mortgageFees : 0,
      hoaFees: workbook.hoaFees ? workbook.hoaFees : 0,
      maintenance: workbook.maintenance ? workbook.maintenance : 0,
      insurance: workbook.insurance ? workbook.insurance : 0,
      electricity: workbook.electricity ? workbook.electricity : 0,
      wifi: workbook.wifi ? workbook.wifi : 0,
      gas: workbook.gas ? workbook.gas : 0,
      other: workbook.other ? workbook.other : 0,
      homeFurnishings: workbook.homeFurnishings ? workbook.homeFurnishings : 0,
      closingCosts: workbook.closingCosts ? workbook.closingCosts : 0,
      occupancyRate: workbook.occupancyRate ? workbook.occupancyRate : 0,
      nightlyRate: workbook.nightlyRate ? workbook.nightlyRate : 0,
      cleaningFees: workbook.cleaningFees ? workbook.cleaningFees : 0,
      bookingsPerMonth: workbook.bookingsPerMonth ? workbook.bookingsPerMonth : 0,
      monthlyRevenues: workbook.monthlyRevenues ? workbook.monthlyRevenues : ([] as number[]),
      name: workbook.name ? workbook.name : '',
      link: workbook.link ? workbook.link : '',
      bedrooms: workbook.bedrooms ? workbook.bedrooms : 0,
      bathrooms: workbook.bathrooms ? workbook.bathrooms : 0,
      size: workbook.size ? workbook.size : 0,
      note: workbook.note ? workbook.note : '',
      address: workbook.address ? workbook.address : '',
      coordinates: workbook.coordinates ? workbook.coordinates : '',
      grossRentalYield: workbook.grossRentalYield ? workbook.grossRentalYield : 0,
      netRentalYield: workbook.netRentalYield ? workbook.netRentalYield : 0,
      netProfitBeforeTax: workbook.netProfitBeforeTax ? workbook.netProfitBeforeTax : 0,
      cashOnCashReturn: workbook.cashOnCashReturn ? workbook.cashOnCashReturn : 0,
      currency: workbook.currency ? workbook.currency : 'USD',
      annualRevenue: workbook.annualRevenue ? workbook.annualRevenue : 0,
    });
  });
  return workbooks;
};

const validateInput = (field: string, newVal: string, oldVal: string) => {
  if (field !== 'name' && field !== 'note') {
    newVal = parseFloat(newVal);
  }
  if (isNaN(newVal) || newVal === oldVal) {
    return false;
  }
  return true;
};

export default MyAnalysisDashboard;
